import {
  Popover,
  PopoverContent,
  OrderedList,
  ListItem,
  UnorderedList,
  Tooltip,
  Box,
  Text,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import GarrazCopyText from "./GaraazCopyText";
import { GrDownload } from "react-icons/gr";
import { getNationalMobileForCopy } from "../../../utils/util";

function copyTextForKey(key: string, value: unknown): string {
  if (typeof value !== "string") return String(value ?? "");
  const normalizedKey = key.trim().toLowerCase();
  const mobileKeys = new Set([
    "mobile number",
    "spoc number",
    "mobile no.",
    "mobile no",
  ]);
  if (mobileKeys.has(normalizedKey)) {
    return getNationalMobileForCopy(value);
  }
  return value;
}

function isTooltipTextValue(value: unknown): value is string | number {
  return typeof value === "string" || typeof value === "number";
}

type Props = {
  value: {}[];
  title: string;
  trigger?: any;
  CopierKeys: string[];
  ArekeysVisible: boolean;
  children?: React.ReactNode;
  titleIcon?: any;
  placement?: any;
  contentMinWidth?: string;
  keyMinWidth?: string;
  onClose?: () => void;
  /** When a row value is a React node, supply the string to copy for that key. */
  copyKeyOverrides?: Record<string, string>;
};

const GaraazGeneralPopover: React.FC<Props> = (props: Props) => {
  const {
    trigger,
    title,
    value,
    CopierKeys,
    ArekeysVisible,
    children,
    titleIcon,
    placement,
    contentMinWidth,
    keyMinWidth,
    onClose,
    copyKeyOverrides,
  } = props;

  return (
    <Popover
      returnFocusOnClose={false}
      placement={placement}
      trigger={trigger ?? "hover"}
      onClose={onClose}
    >
      {children}

      <PopoverContent
        zIndex={"popover"}
        style={{
          padding: "10px",
          height: "100%",
          minWidth: contentMinWidth ?? undefined,
        }}
      >
        <HStack mb="2">
          <Text
            color={"#4A5567"}
            fontSize={"lg"}
            fontWeight={"bold"}
            textTransform={"capitalize"}
          >
            {title}
          </Text>
          {titleIcon}
        </HStack>

        {value.length !== 1 && (
          <Box textColor={"#4A5567"}>
            <OrderedList fontSize={"sm"}>
              {value?.map((item: any, index: number) => (
                <ListItem
                  borderTop={index >= 1 ? "1px solid lightgrey" : "none"}
                  key={index}
                >
                  <UnorderedList
                    key={index}
                    listStyleType={"none"}
                    marginLeft={0}
                    paddingTop={index >= 1 ? "10px" : "0px"}
                    marginBottom={"1em"}
                  >
                    {Object?.entries(item)?.map(
                      ([key, value]: [any, any], index) => {
                        return (
                          <ListItem
                            display={"flex"}
                            key={index}
                            alignItems={"center"}
                          >
                            {ArekeysVisible && (
                              <>
                                {" "}
                                <Text
                                  fontSize={"sm"}
                                  minWidth={keyMinWidth ?? "90px"}
                                  whiteSpace="nowrap"
                                >
                                  {key}
                                </Text>
                                <Text fontSize={"sm"} marginRight={"5px"}>
                                  :
                                </Text>{" "}
                              </>
                            )}

                            <HStack spacing={1} alignItems="center">
                              {isTooltipTextValue(value) ? (
                                <Tooltip label={`${value}`}>
                                  <Text fontSize={"sm"} noOfLines={1}>
                                    {value}
                                  </Text>
                                </Tooltip>
                              ) : (
                                <Box fontSize={"sm"}>{value as React.ReactNode}</Box>
                              )}

                              {key === "insuranceClaimId" &&
                                CopierKeys.includes(key) &&
                                value && (
                                  <GarrazCopyText
                                    text={
                                      copyKeyOverrides?.[key] ??
                                      copyTextForKey(key, value)
                                    }
                                    popOverMessage={`${key} Copied`}
                                  />
                              )}
                            </HStack>
                          </ListItem>
                        );
                      }
                    )}
                  </UnorderedList>
                </ListItem>
              ))}
            </OrderedList>
          </Box>
        )}

        {value.length === 1 && (
          <Box textColor={"#4A5567"}>
            <UnorderedList
              listStyleType={"none"}
              marginLeft={0}
              paddingTop={"0px"}
              marginBottom={"1em"}
              fontSize={"sm"}
            >
              {Object.entries(value[0]).map(
                ([key, value]: [any, any], index) => {
                  return (
                    <ListItem
                      display={"flex"}
                      key={index.toString()}
                      alignItems={"center"}
                    >
                      {ArekeysVisible && (
                        <>
                          {" "}
                          <Text
                            fontSize={"sm"}
                            minWidth={keyMinWidth ?? "90px"}
                            fontWeight={"normal"}
                            textTransform={"capitalize"}
                            whiteSpace="nowrap"
                          >
                            {key}
                          </Text>
                          <Text marginRight={"5px"}>:</Text>{" "}
                        </>
                      )}
                      <HStack spacing={1} alignItems="center">
                        {isTooltipTextValue(value) ? (
                          <Tooltip label={`${value}`}>
                            <Text
                              fontSize={"sm"}
                              noOfLines={1}
                              fontWeight={"normal"}
                              textTransform={"capitalize"}
                            >
                              {value}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Box fontSize={"sm"} fontWeight={"normal"}>
                            {value as React.ReactNode}
                          </Box>
                        )}

                        {CopierKeys.includes(key) && value && (
                          <GarrazCopyText
                            text={
                              copyKeyOverrides?.[key] ??
                              copyTextForKey(key, value)
                            }
                            popOverMessage={`${key} Copied`}
                          />
                        )}
                      </HStack>
                    </ListItem>
                  );
                }
              )}
            </UnorderedList>
          </Box>
        )}

        {(!value || value.length === 0) && (
          <Text
            color={"#4a5568"}
            textTransform={"capitalize"}
            fontWeight={"normal"}
          >
            No {title}
          </Text>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default GaraazGeneralPopover;